import React from 'react';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle, XCircle } from 'lucide-react';

/**
 * Admin → Approvals section.
 * Renders three tabs: Teacher Leave, Class Reschedule, Teacher Change.
 * All data + handlers are passed in via props so the component stays presentational.
 */
export const ApprovalsSection = ({
  approvalData,
  approvalsTab,
  setApprovalsTab,
  teacherChangeReqs,
  teachers,
  handleLeaveAction,
  handleApprovalRescheduleAction,
  handleApproveTeacherChange,
  handleRejectTeacherChange,
}) => {
  const pendingTeacherChange = teacherChangeReqs.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Approvals</h1>
          <p className="text-sm text-slate-500 font-semibold">Manage teacher leave, class reschedule and teacher-change requests</p>
        </div>
        {approvalData.pending_count > 0 && (
          <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">{approvalData.pending_count} pending</span>
        )}
      </div>

      <Tabs value={approvalsTab} onValueChange={setApprovalsTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="leave" data-testid="approvals-leave-tab">Teacher Leave ({approvalData.leave_requests.length})</TabsTrigger>
          <TabsTrigger value="reschedule" data-testid="approvals-reschedule-tab">Class Reschedule ({approvalData.reschedule_requests.length})</TabsTrigger>
          <TabsTrigger value="teacher-change" data-testid="approvals-teacher-change-tab">Teacher Change ({pendingTeacherChange})</TabsTrigger>
        </TabsList>

        <TabsContent value="leave" className="space-y-3 mt-4">
          {approvalData.leave_requests.length === 0 ? (
            <EmptyState message="No leave requests" />
          ) : (
            approvalData.leave_requests.map(req => (
              <div key={req.id} className="nb-card p-5" data-testid={`leave-approval-${req.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900">{req.teacher_name}</span>
                      <StatusBadge status={req.status} />
                    </div>
                    <p className="text-sm text-slate-500 font-semibold mb-2">{req.reason || 'No reason provided'}</p>
                    <div className="flex flex-wrap gap-1">
                      {(req.slots || []).map((s, i) => (
                        <span key={i} className="text-xs bg-slate-100 text-slate-500 font-semibold px-2 py-0.5 rounded">{s.date} {s.start_time}-{s.end_time}</span>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 font-semibold mt-2">{new Date(req.created_at).toLocaleDateString()}</p>
                  </div>
                  {req.status === 'pending' && (
                    <div className="flex gap-2 ml-4">
                      <Button size="sm" onClick={() => handleLeaveAction(req.id, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid={`approve-leave-${req.id}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleLeaveAction(req.id, 'rejected')} className="border-red-400/30 text-red-600 hover:bg-red-500/10 text-xs" data-testid={`reject-leave-${req.id}`}>
                        <XCircle className="w-3 h-3 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="reschedule" className="space-y-3 mt-4">
          {approvalData.reschedule_requests.length === 0 ? (
            <EmptyState message="No reschedule requests" />
          ) : (
            approvalData.reschedule_requests.map(req => (
              <div key={req.id} className="nb-card p-5" data-testid={`reschedule-approval-${req.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900">{req.requester_name || req.student_name || 'Unknown'}</span>
                      <span className="text-xs text-slate-500 font-semibold">({req.requested_by || req.type || 'request'})</span>
                      <StatusBadge status={req.status} />
                    </div>
                    <p className="text-sm text-slate-500 font-semibold">{req.reason || 'No reason'}</p>
                    {req.type === 'recurring_schedule_change' ? (
                      <div className="mt-2 text-xs text-slate-500 font-semibold">
                        <p>Current: {(req.current_days || []).join(', ')} at {req.current_time}</p>
                        <p>Requested: {(req.new_days_of_week || []).join(', ')} at {req.new_time}</p>
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-slate-500 font-semibold">
                        {req.proposed_date && <p>Proposed: {req.proposed_date} at {req.proposed_time}</p>}
                      </div>
                    )}
                    <p className="text-xs text-slate-500 font-semibold mt-1">{new Date(req.created_at).toLocaleDateString()}</p>
                  </div>
                  {req.status === 'pending' && (
                    <div className="flex gap-2 ml-4">
                      <Button size="sm" onClick={() => handleApprovalRescheduleAction(req.id, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid={`approve-resched-${req.id}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleApprovalRescheduleAction(req.id, 'rejected')} className="border-red-400/30 text-red-600 hover:bg-red-500/10 text-xs" data-testid={`reject-resched-${req.id}`}>
                        <XCircle className="w-3 h-3 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="teacher-change" className="space-y-3 mt-4" data-testid="teacher-change-tab-content">
          {teacherChangeReqs.length === 0 ? (
            <EmptyState message="No teacher-change requests" />
          ) : (
            teacherChangeReqs.map(req => (
              <div key={req.id} className="nb-card p-5" data-testid={`teacher-change-${req.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-bold text-slate-900">{req.student_name}</span>
                      <span className="text-xs text-slate-500">({req.citt_id})</span>
                      <StatusBadge status={req.status} />
                    </div>
                    <div className="text-sm text-slate-700 mt-2 space-y-1">
                      <p><span className="font-semibold">Current teacher:</span> {req.current_teacher_name || '—'}</p>
                      {req.requested_teacher_id && <p><span className="font-semibold">Requested:</span> {req.requested_teacher_id}</p>}
                      {req.new_teacher_name && <p><span className="font-semibold">Approved to:</span> {req.new_teacher_name}</p>}
                      <p className="text-slate-600"><span className="font-semibold">Reason:</span> {req.reason || '—'}</p>
                      {req.admin_note && <p className="text-red-600 text-xs"><span className="font-semibold">Admin note:</span> {req.admin_note}</p>}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">{new Date(req.created_at).toLocaleString()}</p>
                  </div>
                  {req.status === 'pending' && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select onValueChange={(v) => handleApproveTeacherChange(req.id, v)}>
                        <SelectTrigger className="h-9 w-44 text-xs" data-testid={`teacher-change-select-${req.id}`}>
                          <SelectValue placeholder="Approve to teacher…" />
                        </SelectTrigger>
                        <SelectContent>
                          {teachers.filter(t => t.id !== req.current_teacher_id).map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" onClick={() => handleRejectTeacherChange(req.id)} className="border-red-400/30 text-red-600 hover:bg-red-500/10 text-xs" data-testid={`reject-teacher-change-${req.id}`}>
                        <XCircle className="w-3 h-3 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div className="nb-card p-12 text-center">
    <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" strokeWidth={2.5} />
    <p className="text-slate-700 font-bold">{message}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const cls = status === 'pending' ? 'bg-amber-100 text-amber-700'
    : status === 'approved' ? 'bg-emerald-100 text-emerald-700'
    : 'bg-red-100 text-red-700';
  return <span className={`px-2 py-0.5 text-xs rounded-full font-bold ${cls}`}>{status}</span>;
};

export default ApprovalsSection;
